"use client";

import * as React from "react";
import {
  motion,
  useMotionValue,
  useTransform,
  animate,
  type PanInfo,
  type MotionValue,
} from "motion/react";
import { cn } from "../../lib/utils";
import { Badge } from "./badge";

interface Slide {
  image: string;
  title: string;
  description: string;
  badge: string;
  url?: string;
}

const slides: Slide[] = [
  {
    image: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=2940&auto=format&fit=crop",
    title: "United Kingdom",
    description: "Experience tailored cybersecurity solutions designed for the UKs specific compliance requirements.",
    badge: "🇬🇧 UK Operations",
    url: "/?region=GB",
  },
  {
    image: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?q=80&w=2942&auto=format&fit=crop",
    title: "India",
    description: "Our core research lab and operations center delivering GenAI cybersecurity solutions.",
    badge: "🇮🇳 India Headquarters",
    url: "https://expectationwalkers.tech",
  },
  {
    image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?q=80&w=2832&auto=format&fit=crop",
    title: "Dubai",
    description: "Enterprise security solutions tailored for businesses across the Middle East.",
    badge: "🇦🇪 Middle East",
    url: "https://smilecyber.tech",
  },
  {
    image: "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?q=80&w=2940&auto=format&fit=crop",
    title: "Malaysia",
    description: "Expanding our footprint with cutting-edge cybersecurity and GenAI solutions across Southeast Asia.",
    badge: "🇲🇾 Southeast Asia",
    url: "https://smilecyber.tech",
  },
  {
    image: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=2940&auto=format&fit=crop",
    title: "United Kingdom",
    description: "Experience tailored cybersecurity solutions designed for the UKs specific compliance requirements.",
    badge: "🇬🇧 UK Operations",
    url: "/?region=GB",
  },
  {
    image: "https://images.unsplash.com/photo-1602216056096-3b40cc0c9944?q=80&w=2942&auto=format&fit=crop",
    title: "India",
    description: "Our core research lab and operations center delivering GenAI cybersecurity solutions.",
    badge: "🇮🇳 India Headquarters",
    url: "https://expectationwalkers.tech",
  },
  {
    image: "https://images.unsplash.com/photo-1518684079-3c830dcef090?q=80&w=2832&auto=format&fit=crop",
    title: "Dubai",
    description: "Enterprise security solutions tailored for businesses across the Middle East.",
    badge: "🇦🇪 Middle East",
    url: "https://smilecyber.tech",
  },
  {
    image: "https://images.unsplash.com/photo-1596422846543-75c6fc197f07?q=80&w=2940&auto=format&fit=crop",
    title: "Malaysia",
    description: "Expanding our footprint with cutting-edge cybersecurity and GenAI solutions across Southeast Asia.",
    badge: "🇲🇾 Southeast Asia",
    url: "https://smilecyber.tech",
  },
];

interface CarouselConfig {
  distanceDivisor: number;
  velocityDivisor: number;
  sensitivity: number;
  xMultiplier: number;
  yMultiplier: number;
  rotationMultiplier: number;
  scaleReduction: number;
}

const getCarouselConfig = (width: number): CarouselConfig => {
  if (width < 640) {
    return {
      distanceDivisor: 120,
      velocityDivisor: 500,
      sensitivity: 180,
      xMultiplier: 90,
      yMultiplier: 20,
      rotationMultiplier: 8,
      scaleReduction: 0.06,
    };
  }
  if (width < 1024) {
    return {
      distanceDivisor: 160,
      velocityDivisor: 650,
      sensitivity: 220,
      xMultiplier: 130,
      yMultiplier: 30,
      rotationMultiplier: 10,
      scaleReduction: 0.09,
    };
  }
  return {
    distanceDivisor: 200,
    velocityDivisor: 800,
    sensitivity: 250,
    xMultiplier: 170,
    yMultiplier: 40,
    rotationMultiplier: 12,
    scaleReduction: 0.12,
  };
};

const CarouselStacked = () => {
  const scrollProgress = useMotionValue(0);
  const startProgress = React.useRef(0);
  const dragStartTime = React.useRef(0);
  const dragStartPos = React.useRef({ x: 0, y: 0 });
  const [windowWidth, setWindowWidth] = React.useState(0);

  const total = slides.length;

  React.useEffect(() => {
    setWindowWidth(window.innerWidth);
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const config = React.useMemo(
    () => getCarouselConfig(windowWidth),
    [windowWidth],
  );

  const handleDragStart = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    startProgress.current = scrollProgress.get();
    dragStartTime.current = Date.now();
    dragStartPos.current = { x: info.point.x, y: info.point.y };
  };

  const handleDragEnd = (
    _: MouseEvent | TouchEvent | PointerEvent,
    info: PanInfo,
  ) => {
    const dragDistance = info.offset.x;
    const velocity = info.velocity.x;
    const dragDuration = Date.now() - dragStartTime.current;

    // Check if it was a tap/click
    if (Math.abs(dragDistance) < 10 && dragDuration < 300) {
      // It was a click. Calculate the current active slide index.
      const p = Math.round(scrollProgress.get());
      let activeIndex = (-p) % total;
      if (activeIndex < 0) activeIndex += total;
      
      const activeSlide = slides[activeIndex];
      if (activeSlide && activeSlide.url) {
         if (activeSlide.url.startsWith('http')) {
             window.open(activeSlide.url, '_blank', 'noopener,noreferrer');
         } else {
             window.location.href = activeSlide.url;
         }
      }
    }

    const distanceShift = -dragDistance / config.distanceDivisor;
    const velocityShift = -velocity / config.velocityDivisor;

    let totalShift = Math.round(distanceShift + velocityShift);
    totalShift = Math.max(-3, Math.min(3, totalShift));

    const target = Math.round(startProgress.current) + totalShift;

    animate(scrollProgress, target, {
      type: "spring",
      stiffness: 200,
      damping: 30,
      mass: 1,
    });
  };

  return (
    <div className="flex flex-col items-center justify-center w-full py-16 overflow-hidden select-none">
      <div className="relative w-full max-w-7xl h-[400px] sm:h-[500px] lg:h-[600px] flex items-center justify-center">
        {/* Transparent Drag Surface */}
        <motion.div
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          onDragStart={handleDragStart}
          onDrag={(_, info) => {
            const delta = -info.delta.x / config.sensitivity;
            scrollProgress.set(scrollProgress.get() + delta);
          }}
          onDragEnd={handleDragEnd}
          className="absolute inset-0 z-50 cursor-grab active:cursor-grabbing"
        />

        {slides.map((slide, i) => (
          <Card
            key={i}
            slide={slide}
            index={i}
            total={total}
            progress={scrollProgress}
            config={config}
          />
        ))}
      </div>
    </div>
  );
};

interface CardProps {
  slide: Slide;
  index: number;
  total: number;
  progress: MotionValue<number>;
  config: CarouselConfig;
}

const Card = ({ slide, index, total, progress, config }: CardProps) => {
  const offset = useTransform(progress, (p) => {
    let diff = (index - p) % total;
    if (diff > total / 2) diff -= total;
    if (diff < -total / 2) diff += total;
    return diff;
  });

  const x = useTransform(offset, (o) => o * config.xMultiplier);
  const rotate = useTransform(offset, (o) => {
    const absO = Math.abs(o);
    if (absO < 0.05) return 0;
    return o * config.rotationMultiplier;
  });
  const y = useTransform(offset, (o) => {
    const absO = Math.abs(o);
    if (absO < 0.05) return 0;
    return absO * config.yMultiplier;
  });
  const scale = useTransform(
    offset,
    (o) => 1 - Math.abs(o) * config.scaleReduction,
  );
  const opacity = useTransform(
    offset,
    [-total / 2, -total / 2 + 0.5, 0, total / 2 - 0.5, total / 2],
    [0, 1, 1, 1, 0],
  );
  const zIndex = useTransform(offset, (o) =>
    Math.round(100 - Math.abs(o) * 10),
  );

  return (
    <motion.div
      style={{
        x,
        rotate,
        y,
        scale,
        opacity,
        zIndex,
      }}
      className={cn(
        "absolute rounded-[24px] overflow-hidden bg-muted group pointer-events-none border border-white/10 shadow-2xl",
        "w-64 h-80 sm:w-[340px] sm:h-[460px] lg:w-[420px] lg:h-[540px]",
      )}
    >
      <img
        src={slide.image}
        alt={slide.title}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none transition-transform duration-700 group-hover:scale-110"
      />

      <motion.div
        style={{
          opacity: useTransform(
            offset,
            [-2, -0.5, 0, 0.5, 2],
            [0.5, 0.2, 0, 0.2, 0.5],
          ),
        }}
        className="absolute inset-0 bg-black pointer-events-none"
      />

      <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent pointer-events-none" />

      <Badge className="absolute top-4 right-4 sm:top-6 sm:right-6 lg:top-8 lg:right-8 px-3 py-1 rounded-full bg-white/10 backdrop-blur-md text-xs sm:text-sm font-bold uppercase tracking-widest text-white border-white/20 shadow-lg">
        {slide.badge}
      </Badge>

      <div className="absolute bottom-6 left-4 right-4 sm:bottom-8 sm:left-6 sm:right-6 lg:bottom-10 lg:left-8 lg:right-8 text-white text-left flex flex-col justify-end">
        <motion.h3
          style={{
            opacity: useTransform(offset, [-0.5, 0, 0.5], [0, 1, 0]),
          }}
          className="text-2xl sm:text-3xl lg:text-4xl font-extrabold leading-tight mb-2 sm:mb-3 drop-shadow-md"
        >
          {slide.title}
        </motion.h3>
        <motion.p
          style={{
            opacity: useTransform(offset, [-0.5, 0, 0.5], [0, 1, 0]),
          }}
          className="hidden sm:block text-xs sm:text-sm text-gray-200 drop-shadow-md font-medium"
        >
          {slide.description}
        </motion.p>
        
        <motion.div
          onClick={() => {
            if (slide.url) {
               if (slide.url.startsWith('http')) {
                   window.open(slide.url, '_blank', 'noopener,noreferrer');
               } else {
                   window.location.href = slide.url;
               }
            }
          }}
           style={{
            opacity: useTransform(offset, [-0.5, 0, 0.5], [0, 1, 0]),
          }}
          className="pointer-events-auto cursor-pointer mt-4 sm:mt-6 flex items-center justify-between px-5 py-3 sm:px-6 sm:py-4 bg-white/10 hover:bg-white/20 rounded-2xl font-bold text-white transition-all backdrop-blur-md border border-white/20 shadow-lg"
        >
          <span className="text-sm sm:text-base tracking-wide">Explore Now</span>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </motion.div>

      </div>
    </motion.div>
  );
};

export default CarouselStacked;
